import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'components';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { useContentContext } from '@qbe/dfc-content';
import { FormCard } from '@qbe/ads-blocks-forms';

import validationSchema, {
  breachCauseSchema,
  breachDetailsSchema,
  breachOtherDetailsSchema,
  breachResolutionSchema,
} from './schema';
import BreachType from './sections/BreachType';
import RootCause from './sections/RootCause';
import BreachDetails from './sections/BreachDetails';
import BreachOtherDetails from './sections/BreachOtherDetails';
import Resolution from './sections/Resolution';
import { useContentComplaintPartners } from 'utils/content';
import { useIsFieldRequired } from 'utils/isFieldRequired';

const TellUsMore = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const form = useForm<FormDataType>({
    mode: 'onTouched',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const breachType = watch('breachType');

  const isDetailsValid = breachDetailsSchema.isValidSync(watch());
  const [showBtnDetails, setShowBtnDetails] = React.useState<boolean>(true);

  const isCauseValid = breachCauseSchema.isValidSync(watch()) && isDetailsValid;
  const [showRootCause, setShowRootCause] = React.useState<boolean>(false);
  const [showBtnCause, setShowBtnCause] = React.useState<boolean>(true);

  const isOtherDetailsValid = breachOtherDetailsSchema.isValidSync(watch()) && isCauseValid;
  const [showOtherDetails, setShowOtherDetails] = React.useState<boolean>(false);
  const [showContinueBtn, setShowContinueBtn] = React.useState<boolean>(true);

  const isResolutionValid = breachResolutionSchema.isValidSync(watch()) && isOtherDetailsValid;
  const [showResolution, setShowResolution] = React.useState<boolean>(false);
  const [showSubmitBtn, setShowSubmitBtn] = React.useState<boolean>(true);

  const handleSubmitDetails = () => {
    setShowBtnDetails(false);
    setShowRootCause(true);
    setShowBtnCause(true);
  };

  const handleRootCauseDetails = () => {
    setShowBtnCause(false);
    setShowOtherDetails(true);
    setShowContinueBtn(true);
  };

  const handleOtherDetails = () => {
    setShowContinueBtn(false);
    setShowResolution(true);
    setShowSubmitBtn(true);
  };

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  return (
    <Form onSubmit={handleSubmit(onComplete)} data-test="tell-us-form">
      <FormCard variant="pageTitle" title={getTextContent('breach.tell.us.more.header')} description="" />

      <BreachType form={form} />

      {breachType && (
        <>
          <BreachDetails
            form={form}
            isFieldRequired={isFieldRequired}
            isValid={isDetailsValid}
            showNextButton={showBtnDetails}
            onSubmit={handleSubmitDetails}
          />

          <RootCause
            form={form}
            show={showRootCause}
            isValid={isCauseValid}
            showNextButton={showBtnCause}
            onSubmit={handleRootCauseDetails}
          />

          <BreachOtherDetails
            form={form}
            show={showOtherDetails}
            isValid={isOtherDetailsValid}
            showContinueButton={showContinueBtn}
            onSubmit={handleOtherDetails}
          />

          <Resolution
            form={form}
            isFieldRequired={isFieldRequired}
            show={showResolution}
            isValid={isResolutionValid}
            showSubmitButton={showSubmitBtn}
          />
        </>
      )}
    </Form>
  );
};

export default TellUsMore;
